import { IconButton } from '@creditornot/cb-button';
import {
  BuildingRestaurant,
  Market,
  MoneyBags,
  Profile,
  Stats,
  Document,
  WoltToken,
  AccountingDocument,
  Cross,
} from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { MenuActionItem } from '@creditornot/cb-menu';
import { Popover } from '@creditornot/cb-popover';
import { useDirection } from '@creditornot/cb-provider';
import { Tag } from '@creditornot/cb-tag';
import React, { ReactElement, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { useRouteMatch, Link } from 'react-router-dom';
import styled from 'styled-components';

import { CashPayment, MapsPin, Translate, CoinStackIcon, MenuItem, WoltStar } from 'common/components/icons';
import { useIsMobile } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';
import { useShowNavigationAnnouncement } from 'modules/experience/hooks/useShowNavigationAnnouncement';
import { useExperienceModuleContext } from 'modules/experience-core';
import { NavigationItem, NavigationKey } from 'modules/experience-core';

type NavigationProps = {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isSidebarOpen: boolean) => void;
  businessNavigationItems: NavigationItem[];
  venueNavigationItems: NavigationItem[];
};

const NavigationContainer = styled.nav<{ isSidebarOpen: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-self: stretch;
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px;

  /*** scrollbar customization ***/
  scrollbar-width: ${({ isSidebarOpen }) => (isSidebarOpen ? 'thin' : 'none')};
`;

const NavigationSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  border-radius: 8px;
  gap: 8px;
  width: 100%;
`;

const NavigationSectionHeader = styled.div`
  display: flex;
  align-self: stretch;
  height: 20px;
  padding: 2px 8px;
  border-radius: 8px;
  gap: 8px;
`;

const NavigationSectionTitle = styled(Typography.Small)`
  color: ${cbSemanticCssVars.textSecondary};
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-inline-start: 2px;
`;

const IconProps = {
  width: 16,
  height: 16,
  fill: 'currentColor',
};

const StyledMenuActionItem = styled(MenuActionItem)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding-block: 8px;
  transition: background-color 150ms ease-in-out;
`;

const TooltipContainer = styled.div`
  display: flex;
  gap: 4px;
  padding: 8px;
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const TooltipText = styled(Typography.Body3)`
  max-width: 298px;
`;

const CloseButton = styled(IconButton)`
  margin-inline-start: auto;
`;

const IconsMappings: Record<NavigationKey, ReactElement> = {
  [NavigationKey.INSIGHTS]: <Stats {...IconProps} />,
  [NavigationKey.BEST_OF_WOLT]: <WoltStar {...IconProps} />,
  [NavigationKey.ACCESS_MANAGEMENT]: <Profile {...IconProps} />,
  [NavigationKey.BUSINESS_DETAILS]: <Market {...IconProps} />,
  [NavigationKey.PURCHASES]: <MoneyBags {...IconProps} />,
  [NavigationKey.TRANSLATION]: <Translate {...IconProps} />,
  [NavigationKey.CAMPAIGNS]: <MenuItem {...IconProps} />,
  [NavigationKey.BRAND_VENUE_GROUPS]: <BuildingRestaurant {...IconProps} />,
  [NavigationKey.PICKING_ROUTE]: <MapsPin {...IconProps} />,
  [NavigationKey.VENUES]: <BuildingRestaurant {...IconProps} />,
  [NavigationKey.SETTLEMENTS]: <CashPayment {...IconProps} />,
  [NavigationKey.LISTING_MANAGER]: <Document {...IconProps} />,
  [NavigationKey.PAYOUT_REPORTS]: <CoinStackIcon {...IconProps} />,
  [NavigationKey.WOLT_CAPITAL]: <WoltToken {...IconProps} />,
  [NavigationKey.PURCHASE_DATA]: <AccountingDocument {...IconProps} />,
};

const NavigationItemAnnouncement = ({ text, tag, dismiss }: { text: string; tag?: string; dismiss: () => void }) => {
  return (
    <TooltipContainer>
      <TooltipContent>
        {tag && <Tag variant="blue">{tag.toLocaleUpperCase()}</Tag>}
        <TooltipText>{text}</TooltipText>
      </TooltipContent>
      <CloseButton children={<Cross />} onClick={dismiss} size="tiny" variant="subdued" />
    </TooltipContainer>
  );
};

const NavigationItem = ({
  item,
  isSidebarOpen,
  setIsSidebarOpen,
  navigationAnnouncement,
}: {
  item: NavigationItem;
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isSidebarOpen: boolean) => void;
  navigationAnnouncement?: {
    text: string;
    navigationKey: NavigationKey;
    tag: string | undefined;
    dismiss: () => void;
  };
}) => {
  const isMobile = useIsMobile();
  const { url: baseUrl } = useRouteMatch();
  const { activeNavigation, setActiveNavigation } = useExperienceModuleContext();
  const handleMenuActionItemClick = (item: NavigationItem) => {
    setActiveNavigation(item);
    isMobile && setIsSidebarOpen(false);
  };

  const tooltipPlacement = useDirection() === 'ltr' ? 'right' : 'left';

  return (
    <Popover
      content={
        navigationAnnouncement ? (
          <NavigationItemAnnouncement
            text={navigationAnnouncement.text}
            dismiss={navigationAnnouncement.dismiss}
            tag={navigationAnnouncement.tag}
          />
        ) : (
          <TooltipContainer>
            <Typography.Body3>
              <FormattedMessage id={item.intlKey} />
            </Typography.Body3>
          </TooltipContainer>
        )
      }
      placement={tooltipPlacement}
      openOnClick={false}
      openOnHover={!navigationAnnouncement && !isMobile && !isSidebarOpen}
      open={navigationAnnouncement && !isMobile ? navigationAnnouncement.navigationKey === item.key : undefined}
      offset={3}
    >
      {({ refProps }) => (
        <Link to={`${baseUrl}${item.link}`} {...refProps}>
          <StyledMenuActionItem
            variant="black"
            selected={activeNavigation?.key === item.key}
            leftContent={<IconContainer>{IconsMappings[item.key]}</IconContainer>}
            onClick={() => handleMenuActionItemClick(item)}
          >
            {isSidebarOpen && (
              <Typography.Body3 fontWeight="normal">
                <FormattedMessage id={item.intlKey} />
              </Typography.Body3>
            )}
          </StyledMenuActionItem>
        </Link>
      )}
    </Popover>
  );
};

const NavigationSection = ({
  header,
  isSidebarOpen,
  setIsSidebarOpen,
  navigationItems,
  navigationAnnouncement,
}: {
  header: ReactNode;
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isSidebarOpen: boolean) => void;
  navigationItems: NavigationItem[];
  navigationAnnouncement?: {
    text: string;
    navigationKey: NavigationKey;
    tag: string | undefined;
    dismiss: () => void;
  };
}) => {
  return (
    <NavigationSectionContainer>
      {isSidebarOpen && (
        <NavigationSectionHeader>
          <NavigationSectionTitle>{header}</NavigationSectionTitle>
        </NavigationSectionHeader>
      )}
      {navigationItems.map(item => (
        <NavigationItem
          item={item}
          key={item.key}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          navigationAnnouncement={navigationAnnouncement}
        />
      ))}
    </NavigationSectionContainer>
  );
};

export const Navigation = ({
  isSidebarOpen,
  setIsSidebarOpen,
  businessNavigationItems,
  venueNavigationItems,
}: NavigationProps) => {
  const navigationAnnouncement = useShowNavigationAnnouncement();

  return (
    <NavigationContainer isSidebarOpen={isSidebarOpen}>
      {businessNavigationItems.length > 0 && (
        <NavigationSection
          header={<FormattedMessage id="experience.merchant" />}
          isSidebarOpen={isSidebarOpen}
          navigationItems={businessNavigationItems}
          setIsSidebarOpen={setIsSidebarOpen}
          navigationAnnouncement={navigationAnnouncement}
        />
      )}
      {venueNavigationItems.length > 0 && (
        <NavigationSection
          header={<FormattedMessage id="experience.venue" />}
          isSidebarOpen={isSidebarOpen}
          navigationItems={venueNavigationItems}
          setIsSidebarOpen={setIsSidebarOpen}
          navigationAnnouncement={navigationAnnouncement}
        />
      )}
    </NavigationContainer>
  );
};
